import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const fields = [
  { name: "name", label: "Full Name", required: true },
  { name: "phone", label: "Phone Number", required: true },
  { name: "whatsapp", label: "WhatsApp Number", required: false },
  { name: "address", label: "Delivery Address", required: true, full: true },
  { name: "city", label: "City", required: true },
  { name: "pincode", label: "Pincode", required: true },
];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { settings } = useSettings();
  const delivery = Number(settings.delivery_charge ?? 75);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", whatsapp: "", address: "", city: "", pincode: "" });
  const [notes, setNotes] = useState("");
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [qrCode, setQrCode] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/qr`).then((r) => setQrCode(r.data.qr_code || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0 && !placed) navigate("/shop");
  }, [items, placed, navigate]);

  if (items.length === 0) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !form[f.name].trim()) {
        toast.error(`Please fill in ${f.label}`);
        return;
      }
    }
    if (!utr.trim()) {
      toast.error("Please enter the UTR / transaction reference number");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          variant_label: i.variant_label,
          price: i.price,
          quantity: i.quantity,
          customization: i.customization || "",
          customization_label: i.customization_label || "",
        })),
        customer: form,
        notes,
        utr: utr.trim(),
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setPlaced(true);
      navigate(`/order-success/${data.order_id}`);
      clearCart();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl animate-fade-in px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="mb-8 font-heading text-4xl text-brand-dark">Checkout</h1>
      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-5 rounded-2xl border border-brand-line bg-white p-6 lg:col-span-2">
          <h2 className="font-heading text-2xl text-brand-dark">Delivery Details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <label className="mb-1.5 block text-sm font-medium text-brand-muted">
                  {f.label} {f.required && <span className="text-brand">*</span>}
                </label>
                <input
                  data-testid={`checkout-${f.name}`}
                  value={form[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-brand-dark outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-brand-gold"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-brand-muted">Order Notes (optional)</label>
              <textarea
                data-testid="checkout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Any special instructions..."
                className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-brand-dark outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-brand-gold"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-2xl border border-brand-line bg-white p-6 lg:col-span-2">
          <h2 className="font-heading text-2xl text-brand-dark">Payment (UPI)</h2>
          <p className="text-sm text-brand-muted">
            {settings.payment_instructions || "Pay via UPI to the ID below or scan the QR code, then enter your UTR / transaction reference below."}
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {settings.upi_id && (
              <div className="rounded-xl bg-brand-gold-light/40 px-4 py-3">
                <p className="text-xs uppercase tracking-widest text-brand-muted">UPI ID</p>
                <p data-testid="checkout-upi-id" className="font-semibold text-brand-dark">{settings.upi_id}</p>
              </div>
            )}
            {qrCode && (
              <img data-testid="checkout-qr" src={qrCode} alt="Payment QR" className="h-32 w-32 rounded-xl border border-brand-line object-contain" />
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-brand-muted">
              UTR / Transaction Reference Number <span className="text-brand">*</span>
            </label>
            <input
              data-testid="checkout-utr"
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              placeholder="Enter the UPI transaction reference after paying"
              className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-brand-dark outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-brand-gold"
            />
            <p className="mt-1.5 text-xs text-brand-muted">
              Enter the UTR after completing your UPI payment. Our team will manually verify the payment — this does not confirm receipt automatically.
            </p>
          </div>
        </div>

        <div className="h-fit rounded-2xl border border-brand-line bg-white p-6">
          <h2 className="font-heading text-2xl text-brand-dark">Your Order</h2>
          <div className="mt-4 space-y-3 border-b border-brand-line pb-4">
            {items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-brand-dark">
                  {i.name} <span className="text-brand-muted">×{i.quantity}</span>
                  <span className="block text-xs text-brand-muted">{i.variant_label}</span>
                  {i.customization && (
                    <span className="block text-xs italic text-brand">
                      {i.customization_label ? `${i.customization_label}: ` : ""}{i.customization}
                    </span>
                  )}
                </span>
                <span className="font-medium text-brand-dark">{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-b border-brand-line py-4 text-sm">
            <div className="flex justify-between text-brand-muted">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-brand-muted">
              <span>Delivery</span>
              <span data-testid="checkout-delivery">{formatPrice(delivery)}</span>
            </div>
          </div>
          <div className="flex justify-between py-4 text-lg font-semibold text-brand-dark">
            <span>Total</span>
            <span data-testid="checkout-final-total">{formatPrice(subtotal + delivery)}</span>
          </div>
          <button
            data-testid="place-order-btn"
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-brand px-6 py-3 font-medium text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60"
          >
            {submitting ? "Placing Order..." : "Place Order"}
          </button>
          <p className="mt-3 text-center text-xs text-brand-muted">
            Pay via UPI/QR and submit your UTR. Payment is verified manually by our team.
          </p>
        </div>
      </form>
    </div>
  );
}
